import moment from 'moment-timezone'

export const FORMAT_24 = 'YYYY-MM-DD HH:mm:ss'
export const FORMAT_DATE = 'YYYY-MM-DD'
export const FORMAT_24_MILLIS = 'YYYY-MM-DD HH:mm:ss.SSS'
export const FORMAT_AMPM = 'YYYY-MM-DD hh:mm:ss A'
export const FORMAT_EPOCH_MILLIS = 'x'
export const FORMAT_ISO = 'YYYY-MM-DDTHH:mm:ss.SSSZ'
export const DEFAULT_FORMAT = FORMAT_24

// LATEST TIME 추가시 convertToISO() 에 case 문 추가필요
export const LATEST_TIME = {
  NOW: 'now',
  MIN_5: 'now-5m',
  MIN_15: 'now-15m',
  MIN_30: 'now-30m',
  MIN_60: 'now-60m',
  DAY_1: 'now-1day',
  DAY_7: 'now-7d',
  MTH_1: 'now-1M',
}

// const FORMATS
const FORMATS = [
  FORMAT_24,
  FORMAT_24,
  FORMAT_24_MILLIS,
  DEFAULT_FORMAT,
  FORMAT_AMPM,
  FORMAT_ISO,
  moment.ISO_8601,
  FORMAT_EPOCH_MILLIS,
]

// time zone parse
function parseLocalTime(time?: string | null): moment.Moment {
  const timezone = moment.tz.guess()
  const rst = time ? moment.tz(time, FORMATS, true, timezone) : moment().tz(timezone)
  return rst
}

// keyword 변환
function convertText(time?: string | number | null): string | undefined {
  if (!time) return

  if (typeof time === 'number') time = time.toFixed(0)

  const t = time.toString().trim()
  let rst: string
  switch (t) {
    case LATEST_TIME.NOW:
      rst = moment().toISOString()
      break
    case LATEST_TIME.MIN_5:
      rst = moment().add(-5, 'minutes').toISOString()
      break
    case LATEST_TIME.MIN_15:
      rst = moment().add(-15, 'minutes').toISOString()
      break
    case LATEST_TIME.MIN_30:
      rst = moment().add(-30, 'minutes').toISOString()
      break
    case LATEST_TIME.MIN_60:
      rst = moment().add(-60, 'minutes').toISOString()
      break
    case LATEST_TIME.DAY_1:
      rst = moment().add(-1, 'days').toISOString()
      break
    case LATEST_TIME.DAY_7:
      rst = moment().add(-7, 'days').toISOString()
      break
    case LATEST_TIME.MTH_1:
      rst = moment().add(-1, 'months').toISOString()
      break
    default:
      rst = t
      break
  }
  return rst
}

export default {
  getTimezone(): string {
    return moment.tz.guess()
  },
  getTimezoneList(): string[] {
    return moment.tz.names()
  },
  getTimezoneOffset(timezone: string): string {
    const offset = moment.tz(timezone).format('Z')
    return offset
  },
  // timezone 값을 이용하지 않고 keyword 변환 - parse - to ISO
  getNoTimezoneTime(time: string | number): string {
    const parseTime = moment(time, FORMATS, true)
    const rst = parseTime.toISOString()
    return rst
  },
  // timezone 값을 이용하지 않고 now time - format
  getNoTimezoneNowTime(): string {
    const parseTime = moment()
    const rst = parseTime.toISOString()
    return rst
  },
  // keyword 변환 - parse - to ISO
  getISOTime(time: string | number): string {
    const convertTime = convertText(time)
    const parseTime = parseLocalTime(convertTime)
    const rst = parseTime.toISOString()
    return rst
  },
  // now time - format
  getNowTime(format?: string): string {
    const parseTime = parseLocalTime(null)
    const rst = parseTime.format(format || DEFAULT_FORMAT)
    return rst
  },
  // keyword 변환 - timezone parse - format
  getLocalTime(time: string | number | null, format?: string): string {
    const convertTime = convertText(time)
    const parseTime = parseLocalTime(convertTime)

    if (!parseTime.isValid()) {
      return ''
    }

    const rst = parseTime.format(format || DEFAULT_FORMAT)
    return rst
  },
  getTimeSeconds(time: string | number): number {
    return Math.floor(this.getTimeMiliseconds(time) / 1000)
  },
  getTimeMiliseconds(time: string | number): number {
    const convertTime = convertText(time)
    const rst = moment(convertTime).valueOf()
    return rst
  },
  // DateTimePicker preset
  getPresetDt(
    diffValue?: number,
    diffUnit?: moment.unitOfTime.DurationConstructor,
    format?: string,
  ): string {
    const parseTime = parseLocalTime(null)
    const m = diffUnit ? parseTime.add(diffValue, diffUnit) : parseTime
    const rst = m.format(format || DEFAULT_FORMAT)
    return rst
  },
  getAddTime(
    add: number,
    unit: moment.unitOfTime.DurationConstructor,
    time: string | number,
    format?: string,
  ): string {
    const convertTime = convertText(time)
    const after = moment(convertTime).add(add, unit).toISOString()
    const parseTime = parseLocalTime(after)
    const rst = parseTime.format(format || DEFAULT_FORMAT)
    return rst
  },
  getDiffDays(start: string | number, end: string | number): number {
    return Math.floor(this.getDiffHours(start, end) / 24)
  },
  getDiffHours(start: string | number, end: string | number): number {
    return Math.floor(this.getDiffMinutes(start, end) / 60)
  },
  getDiffMinutes(start: string | number, end: string | number): number {
    return Math.floor(this.getDiffSeconds(start, end) / 60)
  },
  getDiffSeconds(start: string | number, end: string | number): number {
    return Math.floor(this.getDiffMillis(start, end) / 1000)
  },
  getDiffMillis(start: string | number, end: string | number): number {
    const st = moment(start, FORMATS, true)
    const ed = moment(end, FORMATS, true)
    return Math.floor(moment.duration(ed.diff(st)).asMilliseconds())
  },
  isValidDateTime(time: string | number | null): boolean {
    if (!time) return false
    const t = moment(time, FORMATS, true)
    return moment(t).isValid()
  },
  isValidDateRange(start: string | number, end: string | number): boolean {
    if (!start || !end) return false
    const transSt = convertText(start)
    const transEd = convertText(end)
    const st = moment(transSt, FORMATS, true)
    const ed = moment(transEd, FORMATS, true)
    return st.isSameOrBefore(ed)
  },
  isLatestTime(time: string): boolean {
    return Object.values(LATEST_TIME).includes(time)
  },
  getDurationString(startTime?: string): string {
    if (!startTime || startTime === '-') return '-'
    const start = moment(startTime)
    if (!start.isValid()) return '-'
    const now = moment()
    const diff = moment.duration(now.diff(start))

    const days = Math.floor(diff.asDays())
    const hours = diff.hours()
    const mins = diff.minutes()

    if (days > 0) {
      return `${days}d ${hours}h`
    } else if (hours > 0) {
      return `${hours}h ${mins}m`
    } else {
      return `${mins}m`
    }
  },
  getSubtractTime(
    time: string | number,
    sub: number,
    unit: moment.unitOfTime.DurationConstructor,
    format?: string,
  ): string {
    const convertTime = convertText(time)
    const before = moment(convertTime).subtract(sub, unit).toISOString()
    const parseTime = parseLocalTime(before)
    const rst = parseTime.format(format || DEFAULT_FORMAT)
    return rst
  },
}
